import { useEffect, useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui';

export interface BusinessRejectModalProps {
  open: boolean;
  memberName: string;
  isSubmitting: boolean;
  errorMessage?: string | null;
  onConfirm: (reason: string | null) => void;
  onCancel: () => void;
}

/**
 * R6 O — 사업자회원 반려 모달. 반려 사유(선택)를 입력한다.
 * 계약: ID-O-be.md POST .../business/reject { reason }.
 */
export const BusinessRejectModal = ({
  open,
  memberName,
  isSubmitting,
  errorMessage,
  onConfirm,
  onCancel,
}: BusinessRejectModalProps) => {
  const { t } = useTranslation('common');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent data-testid="business-reject-modal" className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('businessMembers.reject.title', '사업자회원 반려')}</DialogTitle>
          <DialogDescription>
            {t('businessMembers.reject.subject', { memberName })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <label
            htmlFor="business-reject-reason"
            className="text-sm"
            style={{ color: 'var(--content-muted)' }}
          >
            {t('businessMembers.reject.reasonLabel', '반려 사유 (선택)')}
          </label>
          <textarea
            id="business-reject-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={1000}
            rows={4}
            disabled={isSubmitting}
            className="w-full rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2"
            style={{
              borderColor: 'var(--divider-strong)',
              background: 'var(--surface-0)',
              color: 'var(--content-strong)',
            }}
            aria-label={t('businessMembers.reject.reasonLabel', '반려 사유 (선택)')}
          />
        </div>

        {errorMessage ? (
          <p
            role="alert"
            aria-live="polite"
            className="rounded-md p-2 text-sm"
            style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
          >
            {errorMessage}
          </p>
        ) : null}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" size="sm" onClick={onCancel} disabled={isSubmitting}>
            {t('businessMembers.cancel', '취소')}
          </Button>
          <Button
            type="button"
            variant="danger"
            size="sm"
            disabled={isSubmitting}
            onClick={() => onConfirm(reason.trim() ? reason.trim() : null)}
          >
            {isSubmitting
              ? t('businessMembers.submitting', '처리 중...')
              : t('businessMembers.reject.confirm', '반려')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
